// @flow

import { useState, useEffect } from 'react';
import { type LookAndFeel, type LookAndFeelLoadingPage, type LookAndFeelRoot } from '@tectonics/look-and-feel';
import { handleError } from '@tectonics/alert-handler';
import { wait, toError, getRetryDelay } from '@tectonics/commons/utils/retry';

type DynamicLookAndFeel = [boolean, ?LookAndFeelLoadingPage, ?LookAndFeelRoot];

const EmptyLoadingPage = () => null;
const defaultLookAndFeelLoadingPageState: () => LookAndFeelLoadingPage = () => EmptyLoadingPage;

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;
const MODULE_LOAD_ERROR_MESSAGE = 'Dynamic Look And Feel - module failed to load';

export function useDynamicLookAndFeel(lookAndFeel: ?LookAndFeel): DynamicLookAndFeel {
  const [loading, setLoading] = useState<boolean>(true);
  const [LafLoadingPage, setLafLoadingPage] = useState<?LookAndFeelLoadingPage>(
    lookAndFeel && lookAndFeel.getLoadingPage ? defaultLookAndFeelLoadingPageState : null
  );
  const [LafMainPage, setLafMainPage] = useState<?LookAndFeelRoot>();

  useEffect(() => {
    if (!lookAndFeel) return;

    (async () => {
      try {
        const getLoadingPage = lookAndFeel.getLoadingPage;

        if (getLoadingPage) {
          setLoading(true);
          const loadingPage = await loadModuleWithRetry(getLoadingPage);
          setLafLoadingPage((): LookAndFeelLoadingPage => loadingPage);
        }
      } catch (e) {
        handleError(e);
      }
    })();
  }, [lookAndFeel]);

  useEffect(() => {
    if (!lookAndFeel) return;

    (async () => {
      try {
        setLoading(true);
        const rootComponent = await loadModuleWithRetry(lookAndFeel.getRootComponent);
        setLafMainPage((): LookAndFeelRoot => rootComponent);
      } catch (e) {
        handleError(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [lookAndFeel]);

  return [loading, LafLoadingPage, LafMainPage];
}

async function loadModuleWithRetry<T>(
  loader: () => Promise<{ default: T }>,
): Promise<T> {
  let lastError: Error | void;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const module = await loader();
      if (!module || !module.default) {
        throw new Error(MODULE_LOAD_ERROR_MESSAGE);
      }
      return module.default;
    } catch (e) {
      lastError = toError(e);
      if (attempt < MAX_RETRIES) {
        await wait(getRetryDelay(RETRY_DELAY_MS, attempt));
      }
    }
  }

  throw lastError || new Error(MODULE_LOAD_ERROR_MESSAGE);
}
